import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { getAccessToken, getCurrentUser, signOut, type AuthUser } from "@/lib/auth";
import { isSupabaseConfigured } from "@/lib/supabase";
import { Copy, LogOut } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
  head: () => ({ meta: [{ title: "Settings — CSaaS" }] }),
});

function SettingsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [revealToken, setRevealToken] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [u, t] = await Promise.all([getCurrentUser(), getAccessToken()]);
      if (cancelled) return;
      setUser(u);
      setToken(t);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const supabaseOn = isSupabaseConfigured();
  const displayToken = token ?? (supabaseOn ? "" : "(mock auth — no token issued)");

  const onSignOut = async () => {
    try {
      await signOut();
      navigate({ to: "/login" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sign-out failed");
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your profile and API access.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input defaultValue={user?.name ?? ""} disabled />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input defaultValue={user?.email ?? ""} disabled />
          </div>
          <Button variant="outline" onClick={onSignOut}>
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">API access token</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Send this as <code className="font-mono text-xs">Authorization: Bearer …</code> when
            calling <code className="font-mono text-xs">/v1/jobs</code>. The token expires
            periodically and is refreshed automatically when you stay signed in.
          </p>
          <div className="flex gap-2">
            <Input
              readOnly
              value={revealToken ? displayToken : displayToken.replace(/./g, "•").slice(0, 48)}
              className="font-mono text-xs"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRevealToken((v) => !v)}
              disabled={!token}
            >
              {revealToken ? "Hide" : "Reveal"}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (!token) return;
                navigator.clipboard.writeText(token);
                toast.success("Copied");
              }}
              disabled={!token}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
